import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { DataTable } from '@/components/ui/DataTable'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/use-toast'
import { monitoring, type SystemMetric, type HealthCheck } from '@/lib/monitoring'
import { featureFlags, type FeatureFlag } from '@/lib/featureFlags'
import { useSystemHealth, useBusinessMetrics, useSystemDiagnostics } from '@/hooks/useSystemDiagnostics'
import { usePlatformSettings, useUpdatePlatformSetting } from '@/hooks/usePlatformSettings'
import { supabase } from '@/integrations/supabase/client'
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient'
import { 
  Activity, AlertTriangle, CheckCircle, Clock, Mail, Settings, Users, Zap, 
  Database, HardDrive, Server, FileText, Download, RefreshCw, Play,
  TrendingUp, TrendingDown, Minus, AlertCircle, ExternalLink
} from 'lucide-react'
import { useErrorStats } from '@/hooks/useErrorMonitoring'
import { useNavigate } from 'react-router-dom'

export default function SystemManagementPage() {
  const [healthStatus, setHealthStatus] = useState<HealthCheck | null>(null)
  const [systemOverview, setSystemOverview] = useState({
    activeUsers: 0,
    totalJobs: 0,
    activeRFQs: 0,
    systemHealth: 'healthy',
    errorRate: 0
  })
  const [metrics, setMetrics] = useState<SystemMetric[]>([])
  const [flags, setFlags] = useState<FeatureFlag[]>([])
  const [newFlag, setNewFlag] = useState({
    flag_name: '',
    description: '',
    enabled: false,
    rollout_percentage: 0
  })
  const [adminEmail, setAdminEmail] = useState('')
  const [diagnosticResults, setDiagnosticResults] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  // Custom hooks for enhanced diagnostics
  const { health, loading: healthLoading, refetch: refetchHealth } = useSystemHealth()
  const { metrics: businessMetrics, loading: businessLoading, refetch: refetchBusiness } = useBusinessMetrics()
  const { runDiagnostic, generateSystemReport, clearCache } = useSystemDiagnostics()
  const { data: errorStats } = useErrorStats()
  const { data: platformSettings } = usePlatformSettings()
  const updateSetting = useUpdatePlatformSetting()
  const navigate = useNavigate()

  useEffect(() => {
    fetchSystemData()
    const interval = setInterval(fetchSystemData, 30000) // Refresh every 30 seconds
    return () => clearInterval(interval)
  }, [])

  const fetchSystemData = async () => {
    try {
      const [healthData, overview, recentMetrics, allFlags] = await Promise.all([
        monitoring.getHealthStatus(),
        monitoring.getSystemOverview(),
        monitoring.getMetrics(undefined, new Date(Date.now() - 24 * 60 * 60 * 1000)), // Last 24 hours
        featureFlags.getAllFlags()
      ])

      setHealthStatus(healthData)
      setSystemOverview(overview)
      setMetrics(recentMetrics)
      setFlags(allFlags)
    } catch (error) {
      console.error('Error fetching system data:', error)
      toast({
        title: 'Error',
        description: 'Failed to fetch system data',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const runSystemDiagnostic = async (testType: 'email' | 'storage' | 'database' | 'all') => {
    try {
      setLoading(true)
      const results = await runDiagnostic(testType, adminEmail) as { overall_status?: string; tests?: Record<string, any> }
      setDiagnosticResults(results)
      
      toast({
        title: 'Diagnostic Complete',
        description: `${testType} diagnostic completed with status: ${results?.overall_status || 'unknown'}`
      })
    } catch (error) {
      console.error('Error running diagnostic:', error)
      toast({
        title: 'Error',
        description: 'Failed to run diagnostic test',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateReport = async () => {
    try {
      const report = await generateSystemReport()
      
      // Create and download the report
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `system-report-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      toast({
        title: 'Report Generated',
        description: 'System report has been downloaded'
      })
    } catch (error) {
      console.error('Error generating report:', error)
      toast({
        title: 'Error',
        description: 'Failed to generate system report',
        variant: 'destructive'
      })
    }
  }

  const handleClearCache = async () => {
    try {
      await clearCache()
      featureFlags.clearCache()
      
      toast({
        title: 'Cache Cleared',
        description: 'Application caches have been cleared'
      })
    } catch (error) {
      console.error('Error clearing cache:', error)
      toast({
        title: 'Error',
        description: 'Failed to clear cache',
        variant: 'destructive'
      })
    }
  }

  const triggerHealthCheck = async () => {
    try {
      await refetchHealth()
      await refetchBusiness()
      
      toast({
        title: 'Health Check Completed',
        description: `System status updated`
      })
    } catch (error) {
      console.error('Error triggering health check:', error)
      toast({
        title: 'Error',
        description: 'Failed to perform health check',
        variant: 'destructive'
      })
    }
  }

  const runScheduler = async () => {
    try {
      const response = await invokeEdgeFunction('scheduler', {
        callerOperation: 'SystemPage.runScheduler',
      })
      if (response.error) throw response.error
      const responseData = response.data as { processed?: number }
      
      toast({
        title: 'Scheduler Executed',
        description: `Processed ${responseData?.processed || 0} jobs`
      })
    } catch (error) {
      console.error('Error running scheduler:', error)
      toast({
        title: 'Error',
        description: 'Failed to run scheduler',
        variant: 'destructive'
      })
    }
  }

  const processEmails = async () => {
    try {
      const response = await invokeEdgeFunction('email-notifications', {
        callerOperation: 'SystemPage.processEmails',
      })
      if (response.error) throw response.error
      const responseData = response.data as { processed?: number }
      
      toast({
        title: 'Email Processing Complete',
        description: `Processed ${responseData?.processed || 0} emails`
      })
    } catch (error) {
      console.error('Error processing emails:', error)
      toast({
        title: 'Error',
        description: 'Failed to process emails',
        variant: 'destructive'
      })
    }
  }

  const createFeatureFlag = async () => {
    if (!newFlag.flag_name.trim()) {
      toast({
        title: 'Error',
        description: 'Flag name is required',
        variant: 'destructive'
      })
      return
    }

    try {
      const { error } = await supabase
        .from('feature_flags')
        .insert({
          flag_name: newFlag.flag_name,
          description: newFlag.description,
          enabled: newFlag.enabled,
          rollout_percentage: newFlag.rollout_percentage,
          target_organizations: [],
          target_users: []
        })

      if (error) throw error

      setNewFlag({
        flag_name: '',
        description: '',
        enabled: false,
        rollout_percentage: 0
      })

      await fetchSystemData()
      featureFlags.clearCache()

      toast({
        title: 'Success',
        description: 'Feature flag created successfully'
      })
    } catch (error) {
      console.error('Error creating feature flag:', error)
      toast({
        title: 'Error',
        description: 'Failed to create feature flag',
        variant: 'destructive'
      })
    }
  }

  const updateFeatureFlag = async (flagId: string, updates: Partial<FeatureFlag>) => {
    try {
      const { error } = await supabase
        .from('feature_flags')
        .update(updates)
        .eq('id', flagId)

      if (error) throw error

      await fetchSystemData()
      featureFlags.clearCache()

      toast({
        title: 'Success',
        description: 'Feature flag updated successfully'
      })
    } catch (error) {
      console.error('Error updating feature flag:', error)
      toast({
        title: 'Error',
        description: 'Failed to update feature flag',
        variant: 'destructive'
      })
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'degraded':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />
      case 'unhealthy':
        return <AlertCircle className="h-4 w-4 text-red-600" />
      default:
        return <Clock className="h-4 w-4 text-gray-400" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800'
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800'
      case 'unhealthy':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getTrendIcon = (value: number, threshold: number) => {
    if (value > threshold) return <TrendingUp className="h-4 w-4 text-red-500" />
    if (value < threshold * 0.5) return <TrendingDown className="h-4 w-4 text-green-500" />
    return <Minus className="h-4 w-4 text-gray-400" />
  }

  if (loading || healthLoading || businessLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">System Diagnostics Dashboard</h1>
          <p className="text-muted-foreground">
            Comprehensive system health monitoring, testing tools, and operational controls
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={triggerHealthCheck} variant="outline">
            <Activity className="h-4 w-4 mr-2" />
            Health Check
          </Button>
          <Button onClick={handleGenerateReport} variant="outline">
            <FileText className="h-4 w-4 mr-2" />
            Generate Report
          </Button>
          <Button onClick={handleClearCache} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Clear Cache
          </Button>
        </div>
      </div>

      {/* Business Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Jobs Created</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{businessMetrics?.jobs_created_24h || 0}</div>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">RFQs Opened</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{businessMetrics?.rfqs_opened_24h || 0}</div>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Bids Submitted</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{businessMetrics?.bids_submitted_24h || 0}</div>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Awards Made</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{businessMetrics?.awards_made_24h || 0}</div>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Completions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{businessMetrics?.completions_24h || 0}</div>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Disputes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold">{businessMetrics?.disputes_opened_24h || 0}</div>
              {getTrendIcon(businessMetrics?.disputes_opened_24h || 0, 5)}
            </div>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="diagnostics" className="space-y-6">
        <TabsList>
          <TabsTrigger value="diagnostics">Manual Testing</TabsTrigger>
          <TabsTrigger value="health">Health Details</TabsTrigger>
          <TabsTrigger value="errors">Errors</TabsTrigger>
          <TabsTrigger value="features">Feature Flags</TabsTrigger>
          <TabsTrigger value="settings">Platform Settings</TabsTrigger>
          <TabsTrigger value="actions">Quick Actions</TabsTrigger>
        </TabsList>

        <TabsContent value="diagnostics" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Manual Testing Tools</CardTitle>
              <CardDescription>
                Run diagnostic tests to verify system component functionality
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="admin-email">Admin Email (for email tests)</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="admin@company.com"
                />
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => runSystemDiagnostic('email')} variant="outline">
                  <Mail className="h-4 w-4 mr-2" />
                  Test Email
                </Button>
                <Button onClick={() => runSystemDiagnostic('storage')} variant="outline">
                  <HardDrive className="h-4 w-4 mr-2" />
                  Test Storage
                </Button>
                <Button onClick={() => runSystemDiagnostic('database')} variant="outline">
                  <Database className="h-4 w-4 mr-2" />
                  Test Database
                </Button>
                <Button onClick={() => runSystemDiagnostic('all')} variant="default">
                  <Play className="h-4 w-4 mr-2" />
                  Run All Tests
                </Button>
              </div>

              {diagnosticResults && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <div className="font-medium">
                        Diagnostic Status: {diagnosticResults.overall_status}
                      </div>
                      <div className="text-sm space-y-1">
                        {Object.entries(diagnosticResults.tests || {}).map(([test, result]: [string, any]) => (
                          <div key={test} className="flex justify-between">
                            <span className="capitalize">{test}:</span>
                            <span className={result.status === 'success' ? 'text-green-600' : 'text-red-600'}>
                              {result.status} - {result.message}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="health" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Detailed Health Status</CardTitle>
              <CardDescription>
                Comprehensive view of all system components
              </CardDescription>
            </CardHeader>
            <CardContent>
              {healthStatus ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(healthStatus.status)}
                      <span className="font-medium">Overall Status</span>
                    </div>
                    <Badge className={getStatusColor(healthStatus.status)}>
                      {healthStatus.status}
                    </Badge>
                  </div>
                  
                  <div className="space-y-3">
                    {healthStatus.checks.map((check, index) => (
                      <div key={index} className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(check.status)}
                          <span className="font-medium capitalize">{check.name.replace('_', ' ')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {check.metrics && (
                            <span className="text-sm text-muted-foreground">
                              {Object.entries(check.metrics).map(([key, value]) => 
                                `${key}: ${value}`
                              ).join(', ')}
                            </span>
                          )}
                          <Badge className={getStatusColor(check.status)}>
                            {check.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">No health data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="features" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Create Feature Flag</CardTitle>
              <CardDescription>
                Add new feature flags to control application features
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="flag-name">Flag Name</Label>
                  <Input
                    id="flag-name"
                    value={newFlag.flag_name}
                    onChange={(e) => setNewFlag({ ...newFlag, flag_name: e.target.value })}
                    placeholder="feature_name"
                  />
                </div>
                <div>
                  <Label htmlFor="rollout">Rollout Percentage</Label>
                  <Input
                    id="rollout"
                    type="number"
                    min="0"
                    max="100"
                    value={newFlag.rollout_percentage}
                    onChange={(e) => setNewFlag({ ...newFlag, rollout_percentage: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newFlag.description}
                  onChange={(e) => setNewFlag({ ...newFlag, description: e.target.value })}
                  placeholder="Describe what this feature flag controls"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="enabled"
                  checked={newFlag.enabled}
                  onCheckedChange={(checked) => setNewFlag({ ...newFlag, enabled: checked })}
                />
                <Label htmlFor="enabled">Enabled</Label>
              </div>
              <Button onClick={createFeatureFlag}>Create Flag</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Feature Flags</CardTitle>
              <CardDescription>
                Manage existing feature flags
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                data={flags}
                columns={[
                  {
                    key: 'flag_name',
                    label: 'Flag Name',
                  },
                  {
                    key: 'description',
                    label: 'Description',
                  },
                  {
                    key: 'enabled',
                    label: 'Enabled',
                    render: (value, item) => (
                      <Switch
                        checked={value as boolean}
                        onCheckedChange={(checked) => updateFeatureFlag(item.id, { enabled: checked })}
                      />
                    ),
                  },
                  {
                    key: 'rollout_percentage',
                    label: 'Rollout %',
                    render: (value, item) => (
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={value as number}
                        onChange={(e) => updateFeatureFlag(item.id, { rollout_percentage: parseInt(e.target.value) || 0 })}
                        className="w-20"
                      />
                    ),
                  },
                ]}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="errors" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Total Errors</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{errorStats?.totalErrors || 0}</div>
                <p className="text-sm text-muted-foreground mt-1">All time</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Unresolved Errors</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">{errorStats?.unresolvedErrors || 0}</div>
                <p className="text-sm text-muted-foreground mt-1">Needs attention</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Critical Errors</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-600">{errorStats?.criticalErrors || 0}</div>
                <p className="text-sm text-muted-foreground mt-1">High priority</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Most Common Errors</CardTitle>
              <CardDescription>Top errors by occurrence count</CardDescription>
            </CardHeader>
            <CardContent>
              {errorStats?.topErrors && errorStats.topErrors.length > 0 ? (
                <div className="space-y-3">
                  {errorStats.topErrors.map((error) => (
                    <div key={error.id} className="flex justify-between items-start p-3 border rounded">
                      <div className="flex-1">
                        <div className="font-medium text-sm truncate">{error.message}</div>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{error.error_type}</Badge>
                          <Badge variant="outline" className="text-xs">{error.severity}</Badge>
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="font-bold">{error.occurrence_count}</div>
                        <div className="text-xs text-muted-foreground">occurrences</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No errors recorded</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Error Monitoring Dashboard</CardTitle>
              <CardDescription>View detailed error tracking and analytics</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/admin/errors')} className="w-full">
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Error Monitoring Dashboard
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Platform Settings</CardTitle>
              <CardDescription>
                Configure platform-wide settings and parameters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {platformSettings && platformSettings.length > 0 ? (
                  <DataTable
                    data={platformSettings}
                    columns={[
                      {
                        key: 'setting_key',
                        label: 'Setting Key',
                        render: (value) => (
                          <span className="font-mono text-sm">{value as string}</span>
                        )
                      },
                      {
                        key: 'setting_value',
                        label: 'Current Value',
                        render: (value, row) => {
                          const setting = row as any;
                          if (setting.data_type === 'boolean') {
                            return (
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={value === 'true'}
                                  onCheckedChange={(checked) => {
                                    updateSetting.mutate({
                                      settingKey: setting.setting_key,
                                      newValue: checked ? 'true' : 'false'
                                    });
                                  }}
                                />
                                <span className="text-sm text-muted-foreground">
                                  {value === 'true' ? 'Enabled' : 'Disabled'}
                                </span>
                              </div>
                            );
                          }
                          return <span className="font-mono text-sm">{value as string}</span>;
                        }
                      },
                      {
                        key: 'description',
                        label: 'Description',
                      },
                      {
                        key: 'data_type',
                        label: 'Type',
                        render: (value) => (
                          <Badge variant="outline">{value as string}</Badge>
                        )
                      },
                      {
                        key: 'updated_at',
                        label: 'Actions',
                        render: (_value, row) => {
                          const setting = row as any;
                          // Boolean settings use the switch toggle, no action button needed
                          if (setting.data_type === 'boolean') {
                            return null;
                          }
                          return (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const newValue = prompt(
                                  `Enter new value for ${setting.setting_key}:\n\nType: ${setting.data_type}\nCurrent: ${setting.setting_value}\n${setting.min_value !== null ? `Min: ${setting.min_value}` : ''}\n${setting.max_value !== null ? `Max: ${setting.max_value}` : ''}`,
                                  setting.setting_value
                                );
                                
                                if (newValue !== null && newValue !== setting.setting_value) {
                                  updateSetting.mutate({
                                    settingKey: setting.setting_key,
                                    newValue
                                  });
                                }
                              }}
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                          );
                        }
                      }
                    ]}
                  />
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      No platform settings configured yet.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="actions" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Background Operations</CardTitle>
                <CardDescription>
                  Manually trigger system operations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={runScheduler} className="w-full" variant="outline">
                  <Clock className="h-4 w-4 mr-2" />
                  Run Scheduler
                </Button>
                <Button onClick={processEmails} className="w-full" variant="outline">
                  <Mail className="h-4 w-4 mr-2" />
                  Process Email Queue
                </Button>
                <Button onClick={handleClearCache} className="w-full" variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Clear Application Cache
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Reports & Exports</CardTitle>
                <CardDescription>
                  Generate system reports and exports
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={handleGenerateReport} className="w-full" variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Download System Report
                </Button>
                <Button onClick={triggerHealthCheck} className="w-full" variant="outline">
                  <Activity className="h-4 w-4 mr-2" />
                  Refresh All Metrics
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}